import React from 'react';
import Rest from 'tools/rest';
import { MapContainer, TileLayer, Marker, Popup, Tooltip } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { PageHeader } from '@ant-design/pro-layout';
import { Radio, Input, Row, Col, Form, Typography, Tag } from 'antd';
import { filter as _filter, replace as _replace } from 'lodash';
import Preloader from 'components/preloader';
import CameraPlayer from 'components/camera-player';

const { Text } = Typography;

const cameraIconUrl =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0Ij48cGF0aCBmaWxsPSJibGFjayIgZD0iTTkuODI4IDVsLTIgMkg0djEyaDE2VjdoLTMuODI4bC0yLTJIOS44Mjh6TTEyIDE3YTQgNCAwIDEgMSAwLTggNCA0IDAgMCAxIDAgOHptMC0yYTIgMiAwIDEgMCAwLTQgMiAyIDAgMCAwIDAgNHoiLz48L3N2Zz4=';

const customIcon = new L.Icon({
  iconUrl: cameraIconUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 20],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const DEFAULT_CENTER = [56.7364, 37.1619];

const cameraTypeLabel = {
  free: { text: 'Городская', color: 'green' },
  home: { text: 'Домовая', color: 'blue' },
  business: { text: 'Частная', color: 'red' },
};

class CamerasMap extends React.Component {
  state = {
    cameras: [],
    loading: false,
    search: {},
    activeCamera: null,
  };

  componentDidMount() {
    document.title += ' | Камеры на карте';
    this.loadCameras();
  }

  componentWillUnmount() {
    document.title = _replace(document.title, ' | Камеры на карте', '');
  }

  loadCameras = () => {
    this.setState({ loading: true });
    Rest.get('/api/v1/cameras.json', { params: { per: 2000, page: 1 } })
      .then((response) => {
        const { cameras } = response.data;
        this.setState({
          cameras: _filter(cameras, (camera) => !!camera.latitude && !!camera.longitude),
          loading: false,
        });
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  };

  handleFilterChange = (key) => (e) => {
    this.setState({
      search: { ...this.state.search, [key]: e.target.value },
      activeCamera: null,
    });
  };

  filteredCameras = () => {
    const { cameras, search } = this.state;
    return _filter(cameras, (camera) => {
      if (search.camera_type && camera.camera_type !== search.camera_type) return false;
      if (search.name) {
        const q = search.name.toLowerCase();
        const haystack = `${camera.name || ''} ${camera.street || ''} ${camera.building || ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  };

  render() {
    const { loading, search, activeCamera } = this.state;
    const cameras = this.filteredCameras();

    return (
      <React.Fragment>
        <Preloader loading={loading}>
          <PageHeader
            title="Камеры на карте"
            style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 0 }}
          />
          <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
            <Col>
              <Form layout="inline">
                <Form.Item>
                  <Input
                    allowClear
                    placeholder="Поиск по названию или адресу"
                    style={{ width: 260 }}
                    value={search.name}
                    onChange={this.handleFilterChange('name')}
                  />
                </Form.Item>
                <Form.Item>
                  <Radio.Group
                    value={search.camera_type}
                    onChange={this.handleFilterChange('camera_type')}
                  >
                    <Radio.Button value={undefined}>Все типы</Radio.Button>
                    <Radio.Button value="free">Городские</Radio.Button>
                    <Radio.Button value="home">Домовые</Radio.Button>
                    <Radio.Button value="business">Частные</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Form>
            </Col>
            <Col>
              <Text>Камер на карте: {cameras.length}</Text>
            </Col>
          </Row>
          <div style={{ position: 'relative', height: 'calc(100vh - 220px)', minHeight: 500 }}>
            <MapContainer style={{ height: '100%', width: '100%' }} center={DEFAULT_CENTER} zoom={12}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              <MarkerClusterGroup
                chunkedLoading
                showCoverageOnHover={false}
                disableClusteringAtZoom={18}
              >
                {cameras.map((camera) => (
                  <Marker
                    key={camera.id}
                    position={[camera.latitude, camera.longitude]}
                    icon={customIcon}
                    eventHandlers={{
                      click: () => this.setState({ activeCamera: camera }),
                    }}
                  >
                    <Tooltip>{camera.name}</Tooltip>
                  </Marker>
                ))}
              </MarkerClusterGroup>
              {activeCamera && (
                <Popup
                  key={activeCamera.id}
                  position={[activeCamera.latitude, activeCamera.longitude]}
                  minWidth={300}
                  maxWidth={340}
                  onClose={() => this.setState({ activeCamera: null })}
                >
                  <div style={{ width: 300 }}>
                    <div style={{ marginBottom: 8 }}>
                      <b>{activeCamera.name}</b>{' '}
                      {cameraTypeLabel[activeCamera.camera_type] && (
                        <Tag color={cameraTypeLabel[activeCamera.camera_type].color}>
                          {cameraTypeLabel[activeCamera.camera_type].text}
                        </Tag>
                      )}
                    </div>
                    {activeCamera.street && (
                      <div style={{ marginBottom: 8 }}>
                        <Text type="secondary">
                          {activeCamera.street}
                          {activeCamera.building && `, ${activeCamera.building}`}
                        </Text>
                      </div>
                    )}
                    <CameraPlayer camera={activeCamera} open height={180} />
                  </div>
                </Popup>
              )}
            </MapContainer>
          </div>
        </Preloader>
      </React.Fragment>
    );
  }
}

export default CamerasMap;
